import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

export interface FormFieldProps {
  label: ReactNode;
  /** Helper text under the control. */
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  optionalLabel?: string;
  /** Right-aligned extra in the label row (e.g. character counter, link). */
  aside?: ReactNode;
  className?: string;
  /** A single control element; id/aria props are injected. */
  children: ReactElement<Record<string, unknown>>;
}

/** Label + control + hint/error, with ids wired for screen readers. */
export function FormField({
  label,
  hint,
  error,
  required,
  optionalLabel,
  aside,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const controlId = (children.props.id as string | undefined) ?? `f${id}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errId = error ? `${controlId}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(' ') || undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children;

  return (
    // content-start: in a paired row (grid sm:grid-cols-2) the stretched field must not spread
    // its rows, or the control without a hint drops below its neighbour.
    <div className={cn('grid content-start gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={controlId} className="text-sm font-medium text-ink-soft">
          {label}
          {required ? (
            <span className="ml-0.5 text-negative" aria-hidden>
              *
            </span>
          ) : optionalLabel ? (
            <span className="ml-1.5 text-xs font-normal text-ink-subtle">{optionalLabel}</span>
          ) : null}
        </label>
        {aside && <div className="text-xs text-ink-subtle">{aside}</div>}
      </div>
      {control}
      {error ? (
        <p
          id={errId}
          role="alert"
          className="flex items-start gap-1.5 text-sm text-negative-ink animate-fade"
        >
          <WarningCircleIcon weight="fill" className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-ink-muted [overflow-wrap:anywhere]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
